import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, Lock, TrendingDown, TrendingUp } from 'lucide-react';
import { monthOf, type Pnl, type PnlBasis } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, Input, Label, ListSkeleton, cn } from '@tailoring-crm/ui';
import { API_BASE_URL } from '../lib/api/base-url.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { pnlCsvPath } from '../lib/expenses/expenses-api.js';
import { useExpenses, usePnl, usePnlRevenueEntries } from '../lib/expenses/use-expenses.js';
import { formatCurrency, formatDate, formatPercent } from '../lib/format.js';

/**
 * Did the shop make money last month, and where did it go?
 *
 * Two things carry this screen and both are stated rather than implied.
 *
 * **The basis is named on the page, not buried in a settings menu.** Cash and accrual give a
 * boutique carrying large unpaid balances two different profit figures and both are true; a
 * screen that picked one silently would hand an owner a number they cannot reconcile against
 * anything they can count. The toggle sits beside the figures with a sentence saying what the
 * active one counts.
 *
 * **Every figure is a button.** Revenue opens the payments (or the documents) behind it;
 * expenses and each category line open the rows behind them. An owner who cannot see why a
 * number is what it is does not believe the number, and a P&L nobody believes is a screen
 * nobody opens twice.
 *
 * It is the owner's alone — the permission matrix has said so since task 4 — so a manager or a
 * karigar who reaches the URL is told plainly rather than shown a page of failed requests.
 */

type Drill = null | { kind: 'revenue' } | { kind: 'expenses'; categoryId?: string };

export function PnlPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [month, setMonth] = useState(() => monthOf(new Date()));
  const [basis, setBasis] = useState<PnlBasis>('cash');
  const [drill, setDrill] = useState<Drill>(null);

  const pnl = usePnl({ month, basis }, { enabled: role === 'owner' });
  const data = pnl.data;

  if (role !== 'owner') {
    return (
      <>
        <PageHeader title={t('pnl.title')} />
        <EmptyState
          icon={Lock}
          title={t('pnl.noAccessTitle')}
          description={t('pnl.noAccessHint')}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('pnl.title')}
        description={t('pnl.description')}
        action={
          <Button asChild variant="outline">
            <a href={`${API_BASE_URL}${pnlCsvPath({ month, basis })}`}>
              <Download aria-hidden="true" />
              {t('pnl.export')}
            </a>
          </Button>
        }
      />

      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pnl-month">{t('pnl.month')}</Label>
            <Input
              id="pnl-month"
              type="month"
              value={month}
              className="max-w-44"
              onChange={(event) => {
                setMonth(event.target.value);
                setDrill(null);
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{t('pnl.basis')}</span>
            <div role="radiogroup" aria-label={t('pnl.basis')} className="flex gap-2">
              {(['cash', 'accrual'] as const).map((one) => (
                <button
                  key={one}
                  type="button"
                  role="radio"
                  aria-checked={basis === one}
                  onClick={() => {
                    setBasis(one);
                    setDrill(null);
                  }}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    basis === one
                      ? 'border-transparent bg-primary text-primary-foreground'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  {t(`pnl.basisLabel.${one}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* What the active basis actually counts, in a sentence. */}
        <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm" role="status">
          <strong className="font-semibold">{t(`pnl.basisLabel.${basis}`)}</strong>{' '}
          {t(`pnl.basisExplainer.${basis}`)}
        </p>

        {pnl.isPending ? <ListSkeleton rows={4} /> : null}

        {data ? (
          <>
            <Figures
              pnl={data}
              onDrill={(next) =>
                setDrill((current) =>
                  current && current.kind === next.kind && !('categoryId' in next) ? null : next,
                )
              }
            />

            <CategoryBreakdown
              pnl={data}
              activeCategoryId={drill?.kind === 'expenses' ? drill.categoryId : undefined}
              onDrill={(categoryId) =>
                setDrill((current) =>
                  current?.kind === 'expenses' && current.categoryId === categoryId
                    ? null
                    : { kind: 'expenses', ...(categoryId ? { categoryId } : {}) },
                )
              }
            />

            {drill?.kind === 'revenue' ? <RevenueEntries month={month} basis={basis} /> : null}
            {drill?.kind === 'expenses' ? (
              <ExpenseEntries
                month={month}
                basis={basis}
                {...(drill.categoryId ? { categoryId: drill.categoryId } : {})}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}

// --- The three headline figures ---------------------------------------------------------

function Figures({
  pnl,
  onDrill,
}: {
  pnl: Pnl;
  onDrill: (drill: { kind: 'revenue' | 'expenses' }) => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <FigureTile
          label={t('pnl.revenue')}
          amountPaise={pnl.totals.revenuePaise}
          changePaise={pnl.change.revenuePaise}
          previousPaise={pnl.previous.totals.revenuePaise}
          previousMonthLabel={pnl.previous.month}
          onClick={() => onDrill({ kind: 'revenue' })}
          drillLabel={t('pnl.showRevenueEntries')}
        />
        <FigureTile
          label={t('pnl.expenses')}
          amountPaise={pnl.totals.expensePaise}
          changePaise={pnl.change.expensePaise}
          previousPaise={pnl.previous.totals.expensePaise}
          previousMonthLabel={pnl.previous.month}
          onClick={() => onDrill({ kind: 'expenses' })}
          drillLabel={t('pnl.showExpenseEntries')}
          /* More spending is not "up and to the right". */
          invertTone
        />
        <FigureTile
          label={t('pnl.profit')}
          amountPaise={pnl.totals.profitPaise}
          changePaise={pnl.change.profitPaise}
          previousPaise={pnl.previous.totals.profitPaise}
          previousMonthLabel={pnl.previous.month}
        />
      </dl>

      {pnl.pendingCount > 0 ? (
        <p className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm" role="status">
          {t('pnl.pendingCaveat', {
            count: pnl.pendingCount,
            amount: formatCurrency(pnl.pendingPaise),
          })}{' '}
          <Link to="/expenses" className="underline underline-offset-2">
            {t('pnl.reviewPending')}
          </Link>
        </p>
      ) : null}
    </>
  );
}

function FigureTile({
  label,
  amountPaise,
  changePaise,
  previousPaise,
  previousMonthLabel,
  onClick,
  drillLabel,
  invertTone = false,
}: {
  label: string;
  amountPaise: number;
  changePaise: number;
  previousPaise: number;
  previousMonthLabel: string;
  onClick?: () => void;
  drillLabel?: string;
  invertTone?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const better = invertTone ? changePaise < 0 : changePaise > 0;
  const Icon = changePaise >= 0 ? TrendingUp : TrendingDown;

  const body = (
    <>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-2xl font-semibold tabular-nums">{formatCurrency(amountPaise)}</dd>
      <dd
        className={cn(
          'mt-1 flex items-center gap-1 text-xs',
          changePaise === 0
            ? 'text-muted-foreground'
            : better
              ? 'text-success'
              : 'text-destructive',
        )}
      >
        <Icon aria-hidden="true" className="size-3.5" />
        {t('pnl.versusPrevious', {
          change: formatCurrency(Math.abs(changePaise)),
          direction: changePaise >= 0 ? t('pnl.up') : t('pnl.down'),
          month: previousMonthLabel,
          previous: formatCurrency(previousPaise),
        })}
      </dd>
    </>
  );

  if (!onClick) {
    return <div className="rounded-lg border border-border p-3">{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={drillLabel}
      className="rounded-lg border border-border p-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {body}
      <span className="mt-1 block text-xs text-primary underline underline-offset-2">
        {drillLabel}
      </span>
    </button>
  );
}

// --- Where the money went ------------------------------------------------------------------

function CategoryBreakdown({
  pnl,
  activeCategoryId,
  onDrill,
}: {
  pnl: Pnl;
  activeCategoryId?: string | undefined;
  onDrill: (categoryId?: string) => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">{t('pnl.byCategory')}</h2>
      {pnl.byCategory.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('pnl.noExpenses')}</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {pnl.byCategory.map((row) => (
            <li key={row.categoryId}>
              <button
                type="button"
                onClick={() => onDrill(row.categoryId)}
                aria-pressed={activeCategoryId === row.categoryId}
                className={cn(
                  'flex w-full flex-col gap-1 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  activeCategoryId === row.categoryId
                    ? 'border-primary bg-accent'
                    : 'border-border hover:bg-accent',
                )}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="font-medium">{row.categoryLabel}</span>
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(row.amountPaise)}
                  </span>
                </span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span
                    aria-hidden="true"
                    className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
                  >
                    <span
                      className="block h-full rounded-full bg-primary"
                      style={{ width: `${Math.round(row.share * 100)}%` }}
                    />
                  </span>
                  {formatPercent(row.share, 0)} · {t('pnl.entryCount', { count: row.count })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// --- The drill-downs -----------------------------------------------------------------------

function RevenueEntries({ month, basis }: { month: string; basis: PnlBasis }): ReactElement {
  const { t } = useTranslation();
  const entries = usePnlRevenueEntries({ month, basis }, { enabled: true });
  const data = entries.data;

  return (
    <section className="flex flex-col gap-2" aria-label={t('pnl.revenueEntries')}>
      <h2 className="text-sm font-semibold">{t('pnl.revenueEntries')}</h2>
      {entries.isPending ? (
        <ListSkeleton rows={3} />
      ) : !data || data.entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('pnl.noRevenueEntries')}</p>
      ) : (
        <>
          <ul className="flex flex-col gap-1.5">
            {data.entries.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
              >
                <span className="min-w-0">
                  <span className="block font-medium">{entry.customerName}</span>
                  <span className="block text-muted-foreground">
                    {entry.reference} · {formatDate(entry.occurredAt)} ·{' '}
                    {t(`pnl.entryKind.${entry.kind}`, { defaultValue: entry.kind })}
                  </span>
                </span>
                <span className="font-semibold tabular-nums">
                  {entry.amountPaise < 0 ? '−' : ''}
                  {formatCurrency(Math.abs(entry.amountPaise))}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground" role="status">
            {t('pnl.entriesTotal', { amount: formatCurrency(data.totalPaise) })}
          </p>
        </>
      )}
    </section>
  );
}

function ExpenseEntries({
  month,
  basis,
  categoryId,
}: {
  month: string;
  basis: PnlBasis;
  categoryId?: string;
}): ReactElement {
  const { t } = useTranslation();
  const list = useExpenses({ month, basis, ...(categoryId ? { categoryId } : {}) });
  const data = list.data;

  return (
    <section className="flex flex-col gap-2" aria-label={t('pnl.expenseEntries')}>
      <h2 className="text-sm font-semibold">{t('pnl.expenseEntries')}</h2>
      {list.isPending ? (
        <ListSkeleton rows={3} />
      ) : !data || data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('pnl.noExpenseEntries')}</p>
      ) : (
        <>
          <ul className="flex flex-col gap-1.5">
            {data.items.map((expense) => (
              <li
                key={expense.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
              >
                <span className="min-w-0">
                  <span className="block font-medium">
                    {expense.vendor ?? expense.categoryLabel}
                  </span>
                  <span className="block text-muted-foreground">
                    {expense.categoryLabel} · {formatDate(expense.incurredOn)}
                    {expense.systemGenerated ? ` · ${t('expenses.systemBadge')}` : ''}
                  </span>
                </span>
                <span className="font-semibold tabular-nums">
                  {formatCurrency(expense.amountPaise)}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground" role="status">
            {t('pnl.entriesTotal', { amount: formatCurrency(data.totalPaise) })}
          </p>
          <Link
            to={`/expenses?month=${month}&basis=${basis}${categoryId ? `&categoryId=${categoryId}` : ''}`}
            className="self-start text-sm text-primary underline underline-offset-2"
          >
            {t('pnl.openInExpenseBook')}
          </Link>
        </>
      )}
    </section>
  );
}

export default PnlPage;
